import { useMemo, useState } from "react";
import { useUiStore } from "@/stores/ui";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { Account, Backtest, Position } from "@/api/types";
import { useApi } from "@/context/ApiContext";
import { Card, CardHead, ExchangeMark, Tag, accountPill, eyebrow, statusPill } from "@/components/ui/kit";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { OrderLedger } from "@/components/OrderLedger";
import { PositionsTable } from "@/components/PositionsTable";
import { accountKindLabel, accountStatusView, accountUsdBalance, isFuturesAccount, providerLabel } from "@/lib/account";
import { isLiveInstance } from "@/lib/live";

type Tab = "positions" | "orders";

const money = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
function Kpi({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <Card className="px-4 py-3.5">
      <div className={eyebrow}>{label}</div>
      <div className="num mt-1.5 text-[22px] font-semibold tracking-[-0.02em]" style={{ color: color || "var(--text-primary)" }}>
        {value}
      </div>
      {sub && <div className="mt-0.5 text-xs text-[var(--text-muted)]">{sub}</div>}
    </Card>
  );
}

function LiveRow({ bt }: { bt: Backtest }) {
  const dot = bt.status === "running" ? "var(--success)" : bt.status === "error" ? "var(--danger)" : "var(--text-disabled)";
  return (
    <Link
      to={`/live/${bt.id}`}
      className="flex items-center gap-3 border-t border-[var(--divider)] px-4 py-2.5 no-underline first:border-t-0 hover:bg-[var(--bg-hover)]"
    >
      <span className="h-[7px] w-[7px] shrink-0 rounded-full" style={{ background: dot }} />
      <span className="min-w-0 flex-1">
        <b className="block truncate text-[13px] font-semibold text-[var(--text-primary)]">{bt.name}</b>
        <span className="block truncate text-xs text-[var(--text-muted)]">
          {bt.symbol} · {bt.timeframe}
        </span>
      </span>
      {statusPill(bt.status, bt.enabled)}
    </Link>
  );
}

export default function ExchangeDetail() {
  const openGoLive = useUiStore((st) => st.openGoLive);
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const api = useApi(["accounts", "backtests"]);
  const account: Account | undefined = api.accounts.find((a: Account) => a.id === id);
  const [tab, setTab] = useState<Tab | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const positions: Position[] = api.positionsByAccount?.[id] || [];
  const balance = accountUsdBalance(api.balancesByAccount?.[id]);
  const live: Backtest[] = useMemo(
    () => (api.backtests || []).filter((bt: Backtest) => isLiveInstance(bt) && bt.accounts?.some((a) => a.account_id === id)),
    [api.backtests, id]
  );

  if (!api.ready) return <Card className="py-8 text-center text-sm text-[var(--text-muted)]">Loading…</Card>;
  if (!account) return <Card className="py-8 text-center text-sm text-[var(--text-muted)]">Account not found.</Card>;

  const futures = isFuturesAccount(account) || positions.length > 0;
  const activeTab: Tab = tab ?? (futures ? "positions" : "orders");
  const view = accountStatusView(account.status, account.enabled);
  const upnl = positions.reduce((sum, p) => sum + (Number(p.unrealized_pnl) || 0), 0);
  const running = live.filter((bt) => bt.status === "running").length;

  async function act(fn: () => Promise<unknown>, fail: string) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : fail);
    } finally {
      setBusy(false);
    }
  }

  const reconnect = () =>
    act(async () => {
      await api.commands.updateAccount({ id, enabled: true });
      await api.commands.restartAccount({ id });
    }, "Reconnect failed");

  return (
    <div className="flex min-w-0 flex-col gap-5">
      {/* Header (design `.live-h`) */}
      <div className="flex flex-wrap items-start justify-between gap-3.5">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            {accountPill(account)}
            <Tag>{`${providerLabel(account.provider)} ${accountKindLabel(account)}`}</Tag>
          </div>
          <div className="mt-2 flex items-center gap-2.5">
            <ExchangeMark provider={account.provider} />
            <h1 className="m-0 truncate text-[22px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">{account.name}</h1>
          </div>
          <p className="m-0 mt-1 text-[13px] text-[var(--text-muted)]">
            {[
              balance ? `${money.format(balance.value)} ${balance.asset}` : null,
              live.length ? `${live.length} live ${live.length === 1 ? "strategy" : "strategies"}` : "No live strategies",
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {account.enabled && (view.tone === "error" || view.tone === "stopped") && (
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void reconnect()}>
              Reconnect
            </button>
          )}
          <Link to={`/integrations/marketplaces/${id}/edit`} className="btn btn-soft">
            Change keys
          </Link>
          <button
            type="button"
            className="btn btn-soft"
            disabled={busy}
            onClick={() => void act(() => api.commands.updateAccount({ id, enabled: !account.enabled }), "Couldn't update")}
          >
            {account.enabled ? "Turn off" : "Turn on"}
          </button>
          <button type="button" className="btn btn-danger-outline" onClick={() => setConfirmDelete(true)}>
            Delete
          </button>
        </div>
      </div>

      {view.tone === "error" && account.status_msg && (
        <div className="rounded-[12px] border border-[color-mix(in_srgb,var(--danger)_40%,var(--border))] bg-[color-mix(in_srgb,var(--danger)_10%,transparent)] px-3.5 py-3 text-[13px]">
          <b className="text-[var(--danger)]">The exchange connection failed.</b>{" "}
          <span className="break-words text-[var(--text-secondary)]">{account.status_msg}</span>
        </div>
      )}
      {error && <div className="rounded-[10px] bg-[var(--danger-bg)] px-3 py-2 text-[13px] text-[var(--danger)]">{error}</div>}

      <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">
        <Kpi
          label="Balance"
          value={balance ? `${money.format(balance.value)}` : "Not connected"}
          sub={balance ? balance.asset : "Shown while the exchange is connected"}
        />
        {futures && (
          <Kpi
            label="Open positions"
            value={String(positions.length)}
            sub={positions.length ? `${upnl >= 0 ? "+" : "−"}$${money.format(Math.abs(upnl))} unrealized` : undefined}
          />
        )}
        <Kpi label="Live strategies" value={String(live.length)} sub={live.length ? `${running} running` : undefined} />
      </div>

      <Card>
        <CardHead title="Live strategies">
          {live.length === 0 && (
            <button type="button" className="btn btn-soft btn-sm" onClick={openGoLive}>
              Go live
            </button>
          )}
        </CardHead>
        {live.length ? (
          <div className="pb-1">
            {live.map((bt) => (
              <LiveRow key={bt.id} bt={bt} />
            ))}
          </div>
        ) : (
          <div className="px-4 pb-4 text-[13px] text-[var(--text-muted)]">No live strategy trades on this exchange yet.</div>
        )}
      </Card>

      <Card>
        <div className="flex gap-0.5 border-b border-[var(--border)] px-3" role="tablist">
          {(futures ? (["positions", "orders"] as const) : (["orders"] as const)).map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={activeTab === t}
              onClick={() => setTab(t)}
              className={`-mb-px border-b-2 px-3 py-2.5 text-[13px] ${
                activeTab === t
                  ? "border-[var(--primary)] text-[var(--text-primary)]"
                  : "border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              }`}
            >
              {t === "positions" ? "Positions" : "Orders"}
              {t === "positions" && positions.length > 0 && (
                <small className="num ml-1.5 text-[var(--text-disabled)]">{positions.length}</small>
              )}
            </button>
          ))}
        </div>
        {activeTab === "positions" ? (
          <PositionsTable positions={positions} />
        ) : (
          <div className="p-3">
            <OrderLedger accountId={id} backtests={api.backtests} compact title="" />
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={confirmDelete}
        title={`Delete "${account.name}"?`}
        description={
          live.length
            ? `${live.length} live ${live.length === 1 ? "strategy uses" : "strategies use"} this exchange and will stop trading. The keys are removed.`
            : "The connection and its keys are removed."
        }
        loading={busy}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() =>
          void act(async () => {
            await api.commands.deleteAccount({ id });
            navigate("/integrations/marketplaces");
          }, "Delete failed")
        }
      />
    </div>
  );
}
